import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Tabs, Tab,
  InputAdornment, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Visibility, Block, History, Search } from '@mui/icons-material';
import axios from 'axios';

function Partners() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', country: '', password: '', postback_url: '',
  });

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get('/admin/partners');
      setPartners(response.data.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = partners.filter(partner => {
    const matchesFilter = filter === 'active' ? partner.status === 'active' : partner.status === 'inactive';
    const matchesSearch = searchTerm === '' || 
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (partner.email && partner.email.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const handleOpenDialog = () => {
    setFormData({ name: '', email: '', phone: '', country: '', password: '', postback_url: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', email: '', phone: '', country: '', password: '', postback_url: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      alert('Please fill in Name, Email and Password');
      return;
    }
    if (formData.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post('/admin/partners', {
        name: formData.name, email: formData.email, phone: formData.phone,
        country: formData.country, password: formData.password,
        postback_url: formData.postback_url, status: 'active',
      });
      setOpenDialog(false);
      setFormData({ name: '', email: '', phone: '', country: '', password: '', postback_url: '' });
      await fetchPartners();
    } catch (error) {
      console.error('Error creating partner:', error);
      alert(`Failed to create partner: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeactivate = async (partner) => {
    if (!window.confirm(`Are you sure you want to deactivate "${partner.name}"?\n\nThis will:\n- Block their login access\n- Disable all tracking links`)) return;
    try {
      await axios.delete(`/admin/partners/${partner.partner_id}`);
      await fetchPartners();
      alert('Partner deactivated successfully!');
    } catch (error) {
      console.error('Error deactivating partner:', error);
      alert(`Failed to deactivate partner: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReactivate = async (partner) => {
    if (!window.confirm(`Are you sure you want to reactivate "${partner.name}"?`)) return;
    try {
      await axios.post(`/admin/partners/${partner.partner_id}/reactivate`);
      await fetchPartners();
      alert('Partner reactivated successfully!');
    } catch (error) {
      console.error('Error reactivating partner:', error);
      alert(`Failed to reactivate partner: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleViewPartner = (partnerId) => {
    navigate(`/partners/${partnerId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Partners
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog} size={isMobile ? 'small' : 'medium'}>
          Add Partner
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
            <Tabs value={filter} onChange={(e, v) => setFilter(v)} sx={{ minHeight: { xs: 36, sm: 48 } }}>
              <Tab label="Active" value="active" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
              <Tab label="Inactive" value="inactive" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
            </Tabs>
            <TextField
              size="small"
              placeholder="Search partners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 250 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {!isMobile && <TableCell>ID</TableCell>}
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPartners.length > 0 ? (
                  filteredPartners.map((partner) => (
                    <TableRow key={partner.partner_id} hover>
                      {!isMobile && <TableCell>{partner.partner_id}</TableCell>}
                      <TableCell>
                        <Link to={`/partners/${partner.partner_id}`} style={{ textDecoration: 'none' }}>
                          <Typography
                            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' }, '&:hover': { textDecoration: 'underline' } }}
                          >
                            {partner.name}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell>
                       <Chip 
                          label={partner.status === 'active' ? 'Active' : 'Inactive'} 
                          color={partner.status === 'active' ? 'success' : 'error'} 
                          size="small"
                          sx={{ color: 'white' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleViewPartner(partner.partner_id)} title="View Details">
                          <Visibility fontSize="small" />
                        </IconButton>
                        {partner.status === 'active' && (
                          <IconButton size="small" color="error" onClick={() => handleDeactivate(partner)} title="Deactivate">
                            <Block fontSize="small" />
                          </IconButton>
                        )}
                        {partner.status === 'inactive' && (
                          <IconButton size="small" color="success" onClick={() => handleReactivate(partner)} title="Reactivate">
                            <History fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 4} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>No {filter} partners found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Partner Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add New Partner</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} margin="normal" required autoFocus />
            <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} margin="normal" required helperText="This will be used for login" />
            <TextField fullWidth label="Password" name="password" type="password" value={formData.password} onChange={handleChange} margin="normal" required helperText="Minimum 6 characters" />
            <TextField fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} margin="normal" />
            <TextField fullWidth label="Country" name="country" value={formData.country} onChange={handleChange} margin="normal" />
            <TextField fullWidth label="Postback URL" name="postback_url" value={formData.postback_url} onChange={handleChange} margin="normal" helperText="Placeholders: {click_id}, {lead_id}, {payout}, {status}, {sub1}-{sub5}, {campaign_id}, {country}, {timestamp}" />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} type="button">Cancel</Button>
            <Button type="submit" variant="contained">Create Partner</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Partners;
